import { inject, Injectable } from '@angular/core';
import { HowdenHttpClientService, IResponse } from '@howdeniberia/core-front';
import { catchError, map, Observable, of } from 'rxjs';
import { API_PATH_APPS } from 'src/app/core/constants';
import { IMasterValue } from 'src/app/core/models/api/master';
import {
  ICalculatorAAxaRequest,
  ICalculatorAPrebalRequest,
  ICalculatorASurneRequest,
  ICalculatorDecAuraRequest,
  ICalculatorDepAxaRequest,
  ICalculatorDepSurneRequest,
  ICalculatorITPmCoegRequest,
  ICalculatorITPmEliteRequest,
  ICalculatorITPrebalRequest,
  ICalculatorITSurnePlusRequest,
  ICalculatorITSurneVipRequest,
  ICalculatorVAllianzRequest,
  ICalculatorVAxaRequest,
  ICalculatorVEStaLuciaRequest,
  ICalculatorVHelvetiaRequest,
  ICalculatorVPrebalInvProRequest,
  ICalculatorVPrebalRequest,
  ICalculatorVStaLuciaRequest,
  ICalculatorVSurneLifeProRequest,
  ICalculatorVSurneRequest,
  ICalculatorVZurichRequest,
  IResponseOutputDto
} from 'src/app/core/models/api/sane-project/arpa-model';
import { CalculatorProjectContract } from './calculator-project.contract';

@Injectable({ providedIn: 'root' })
export class CalculatorProjectService implements CalculatorProjectContract {
  private http = inject(HowdenHttpClientService);

  private readonly apiBase = API_PATH_APPS.API_PERSONAL_INSURANCE;
  private readonly prefix = '/api/arpaprojects/';

  private postCalc<T>(endpoint: string, body: unknown): Observable<IResponse<T>> {
    return this.http.post<IResponse<T>>(
      this.apiBase,
      `${this.prefix}${endpoint}`,
      body
    );
  }

  getResult(path: string, request: unknown): Observable<IResponse<IResponseOutputDto>> {
    return this.postCalc<IResponseOutputDto>(path, request);
  }

  resultBonusVAllianz(req: ICalculatorVAllianzRequest) { return this.postCalc<IResponseOutputDto>('get-allianz-life', req); }
  resultBonusVAxa(req: ICalculatorVAxaRequest) { return this.postCalc<IResponseOutputDto>('get-axa-life', req); }
  resultBonusVStaLucia(req: ICalculatorVStaLuciaRequest) { return this.postCalc<IResponseOutputDto>('get-stalucia-life', req); }
  resultBonusVSurne(req: ICalculatorVSurneRequest) { return this.postCalc<IResponseOutputDto>('get-surne-life', req); }
  resultBonusVSurneLifePro(req: ICalculatorVSurneLifeProRequest) { return this.postCalc<IResponseOutputDto>('get-surne-life-pro', req); }
  resultBonusVHelvetia(req: ICalculatorVHelvetiaRequest) { return this.postCalc<IResponseOutputDto>('get-helvetia-life', req); }
  resultBonusVPrebal(req: ICalculatorVPrebalRequest) { return this.postCalc<IResponseOutputDto>('get-prebal-life', req); }
  resultBonusVPrebalInvPro(req: ICalculatorVPrebalInvProRequest) { return this.postCalc<IResponseOutputDto>('get-prebal-life-disability', req); }
  resultBonusVZurich(req: ICalculatorVZurichRequest) { return this.postCalc<IResponseOutputDto>('get-zurich-life', req); }
  resultBonusVEStaLucia(req: ICalculatorVEStaLuciaRequest) { return this.postCalc<IResponseOutputDto>('get-stalucia-wholelife', req); }

  resultBonusAAxa(req: ICalculatorAAxaRequest) { return this.postCalc<IResponseOutputDto>('get-axa-accident', req); }
  resultBonusASurne(req: ICalculatorASurneRequest) { return this.postCalc<IResponseOutputDto>('get-surne-accident', req); }
  resultBonusAPrebal(req: ICalculatorAPrebalRequest) { return this.postCalc<IResponseOutputDto>('get-prebal-accident', req); }

  resultBonusITPrebal(req: ICalculatorITPrebalRequest) { return this.postCalc<IResponseOutputDto>('get-prebal-ti', req); }
  resultBonusITPmElite(req: ICalculatorITPmEliteRequest) { return this.postCalc<IResponseOutputDto>('get-pm-ti-elite', req); }
  resultBonusITPmCoeg(req: ICalculatorITPmCoegRequest) { return this.postCalc<IResponseOutputDto>('get-pm-ti-coeg', req); }
  resultBonusITSurnePlus(req: ICalculatorITSurnePlusRequest) { return this.postCalc<IResponseOutputDto>('get-surne-ti-plus', req); }
  resultBonusITSurneVip(req: ICalculatorITSurneVipRequest) { return this.postCalc<IResponseOutputDto>('get-surne-ti-vip', req); }

  resultBonusDepAxa(req: ICalculatorDepAxaRequest) { return this.postCalc<IResponseOutputDto>('get-axa-dependency', req); }
  resultBonusDepSurne(req: ICalculatorDepSurneRequest) { return this.postCalc<IResponseOutputDto>('get-surne-dependency', req); }

  resultBonusDecAura(req: ICalculatorDecAuraRequest) { return this.postCalc<IResponseOutputDto>('get-aura-death', req); }

  private normalizeDropdown(raw: any[]): IMasterValue[] {
    return (raw ?? []).map(item => ({
      id: (item?.id ?? item?.value)?.toString() ?? '',
      description: item?.description ?? item?.text ?? ''
    }));
  }

  private loadDropdown(path: string): Observable<IMasterValue[]> {
    return this.http.get<IResponse<IMasterValue[]>>(this.apiBase, path).pipe(
      map(res => this.normalizeDropdown(res?.result ?? [])),
      catchError(() => of([]))
    );
  }

  getAccidentContagions(clientId: number, accidentType: number | string, regime: string): Observable<boolean> {
    const reg = encodeURIComponent((regime ?? '').trim());
    if (!clientId || !reg) { return of(false); }

    const typeNumber = this.toAccidentTypeNumber(accidentType);
    if (!typeNumber) { return of(false); }

    return this.http.get<IResponse<boolean>>(
      this.apiBase,
      `api/general/${clientId}/accident-types/${typeNumber}/regimes/${reg}/contagions`
    ).pipe(
      map(res => !!res?.result),
      catchError(() => of(false))
    );
  }

  /**
   * AXA - HIV/Hepatitis capital calculated by backend.
   * Backend route:
   * GET api/general/capital/contagion/hiv-hepatitis/death/{deathCapital}/disability/{disabilityCapital}
   *
   * Note: returns Response<decimal>. We map it into IMasterValue[] for mat-select compatibility.
   */
  getAxaHivHepatitisCapital(deathCapital: number, disabilityCapital: number): Observable<IMasterValue[]> {
    const death = Number(deathCapital);
    const disability = Number(disabilityCapital);

    if (!Number.isFinite(death) || !Number.isFinite(disability)) {
      return of([]);
    }

    return this.http.get<IResponse<number>>(
      this.apiBase,
      `api/general/capital/contagion/hiv-hepatitis/death/${death}/disability/${disability}`
    ).pipe(
      map(res => {
        const value = res?.result;
        if (value === null || value === undefined || !Number.isFinite(Number(value))) {
          return [];
        }
        const id = String(value);
        return [{ id, description: id }];
      }),
      catchError(() => of([]))
    );
  }

  private toAccidentTypeNumber(v: number | string): number | null {
    if (typeof v === 'number') {
      return Number.isFinite(v) && v > 0 ? v : null;
    }

    switch ((v ?? '').toString().trim().toLowerCase()) {
      case 'axa': return 1;
      case 'surne': return 2;
      case 'prebal': return 3;
      default: return null;
    }
  }

  getSelectDepAxaSevereDependency(): Observable<IMasterValue[]> {
    return this.loadDropdown('api/general/dependency/axa/capitals');
  }

  getSelectDepSurneCapitalIncome(): Observable<IMasterValue[]> {
    return this.loadDropdown('api/general/dependency/capitals-rents/description');
  }

  getSelectSurneSevereDependency(typeDependency: number): Observable<IMasterValue[]> {
    return this.loadDropdown(`api/general/dependency/surne/capitals-rents/${typeDependency}`);
  }

  getSelectFranchise(): Observable<IMasterValue[]> {
    return this.loadDropdown('api/general/franchises');
  }

  getSelectDuration(): Observable<IMasterValue[]> {
    return this.loadDropdown('api/general/durations');
  }

  getSAxaFamilyAssistant(): Observable<IMasterValue[]> {
    return of([
      { id: '0', description: 'No incluida' },
      { id: '1', description: 'Incluida' }
    ]);
  }

  getSPrebalHIVHepatitis(): Observable<IMasterValue[]> {
    return this.loadDropdown('api/general/prebal/hiv-hepatitis-transmission');
  }

  getSSurneHIVHepatitis(): Observable<IMasterValue[]> {
    return this.loadDropdown('api/general/surne/hiv-hepatitis-transmission');
  }


  getSPrebalCapitalHealthCareAccidents(): Observable<IMasterValue[]> {
    return of([
      { id: '0', description: 'No' },
      { id: '1', description: 'Sí' }
    ]);
  }

  getSHasSelectedCapitalDisability(): Observable<IMasterValue[]> {
    return of([
      { id: '0', description: 'No incluida' },
      { id: '1', description: 'Incluida' }
    ]);
  }

  getSSurneCapitalHealthDisabilityType(): Observable<IMasterValue[]> {
    return this.loadDropdown('api/general/surne/medical-assistance-accidents');
  }

  getSSurneDisabilityType(): Observable<IMasterValue[]> {
    return this.loadDropdown('api/projects/accidents/surne/disability-types');
  }

  getSPrebalDisabilityType(): Observable<IMasterValue[]> {
    return this.loadDropdown('api/projects/accidents/prebal/disability-types');
  }

  getSChargeable(): Observable<IMasterValue[]> {
    return of([
      { id: '0', description: 'No' },
      { id: '1', description: 'Sí' }
    ]);
  }

  /**
   * Master dispatcher used by CalculatorProductViewModel.
   * Keep the keys stable; only swap implementations/endpoints.
   *
   * IMPORTANT:
   * - We do NOT add AXA HIV/Hepatitis here because it depends on (deathCapital, disabilityCapital),
   *   which is not part of the generic params contract. Use getAxaHivHepatitisCapital(...) directly from the component.
   */
  getMasterValues(masterKey: string, params?: { professionId?: number; regime?: string }): Observable<IMasterValue[]> {
    if (masterKey === 'S_AXA_CAPITALDISABILITYTYPE') {
      const professionId = params?.professionId;
      const regime = params?.regime?.trim();

      if (!professionId || !regime) {
        return of([]);
      }

      return this.loadDropdown(`api/arpaclients/${professionId}/regimes/${encodeURIComponent(regime)}/axa-invalidities`);
    }

    if (masterKey === 'S_AXA_CAPITALFAMILYACCIDENTASSISTANCE') {
      return this.getSAxaFamilyAssistant();
    }

    if (masterKey === 'S_PREBAL_HASSELECTEDCAPITALDISABILITY') {
      return this.getSHasSelectedCapitalDisability();
    }

    if (masterKey === 'S_PREBAL_CAPITALHEALTHCAREACCIDENTS') {
      return this.getSPrebalCapitalHealthCareAccidents();
    }

    switch (masterKey) {
      case 'S_SURNE_CAPITALABSOLUTEDISABILITYTYPE':
        return this.loadDropdown(`api/arpaclients/${params!.professionId}/surne-invalidities`);
      case 'S_PREBAL_CAPITALDISABILITYTYPE':
        return this.loadDropdown(`api/arpaclients/${params!.professionId}/prebal-invalidities`);
      default:
        return of([]);
    }
  }

  getDepAxaGreatDependency(value: string): number {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n * 2 : 0;
  }

  getDepSurneGreatDependency(value: string): number {
    return this.getDepAxaGreatDependency(value);
  }

  compareByIdNumber = (a: string | number, b: string | number): boolean => {
    const aDefined = a !== null && a !== undefined;
    const bDefined = b !== null && b !== undefined;
    return aDefined && bDefined ? a.toString() === b.toString() : a === b;
  };

  private toComparableString(v: string | number | boolean | null | undefined): string {
    if (typeof v === 'boolean') { return v ? '1' : '0'; }
    if (v === null || v === undefined) { return ''; }
    return String(v);
  }

  compareByIdBoolean(a: string | number | boolean, b: string | number | boolean): boolean {
    if (typeof a === 'boolean' && typeof b === 'boolean') {
      return a === b;
    }
    return this.toComparableString(a as any) === this.toComparableString(b as any);
  }

  checkBonus(
    life: any, accidents: any, it: any, lifeWhole: any, deaths: any, dependency: any
  ): { byCategory: Record<string, number>; total: number; hasAny: boolean } {
    const toArray = (v: unknown): number[] => {
      if (Array.isArray(v)) { return v as number[]; }
      if (v === null || v === undefined) { return []; }
      return [Number(v)];
    };

    const sum = (arr: number[]) =>
      arr.reduce((acc, n) => acc + (Number.isFinite(n) ? n : 0), 0);

    const byCategory = {
      life: sum(toArray(life)),
      accidents: sum(toArray(accidents)),
      it: sum(toArray(it)),
      lifeWhole: sum(toArray(lifeWhole)),
      deaths: sum(toArray(deaths)),
      dependency: sum(toArray(dependency))
    };

    const total = Object.values(byCategory).reduce((a, b) => a + b, 0);
    return { byCategory, total, hasAny: total > 0 };
  }
}
